/**
 * What may be uploaded as a product's walkthrough, and what it is called.
 *
 * ADR 0105 put the three videos in by migration and said plainly that stops
 * being the right trade at about ten. Jonathon, 2026-09-05: "yes please build
 * it to where we can upload videos to products." So the rules move here, where
 * they can be tested, rather than living in the upload button.
 *
 * The limits exist for one reader: a homeowner on a phone, on mobile data,
 * standing in their own driveway while a rep waits. Every rule below is that
 * person's experience expressed as a number.
 */

/**
 * The ceiling, and it is not arbitrary.
 *
 * Supabase's own default object limit is 50 MB, so anything larger fails in the
 * network rather than in the app, with a message nobody can act on. Refusing it
 * here means the rep is told what to do instead of watching a progress bar die
 * at 94%.
 */
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

/**
 * Where a video stops being comfortable long before it stops being possible.
 *
 * The three shipped with ADR 0105 are 11–16 MB for a minute of 720p. Twice that
 * is a warning, not a refusal: a rep who has a reason is not blocked, they are
 * told what it will cost the customer.
 */
export const LARGE_VIDEO_BYTES = 25 * 1024 * 1024;

export const MAX_POSTER_BYTES = 5 * 1024 * 1024;

/**
 * What a browser will actually play everywhere.
 *
 * MP4 only. An iPhone's `.mov` is usually HEVC, which Chrome on most Androids
 * and on Windows will not play — the customer would get a black box on a
 * proposal the rep is standing beside. Taking it and calling it `.mp4` does
 * not change what is inside it.
 */
export const VIDEO_TYPES = ["video/mp4"] as const;
export const POSTER_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

export interface UploadCandidate {
  name: string;
  type: string;
  size: number;
}

export type VideoCheck =
  { ok: true; warning: string | null } | { ok: false; problem: string };

const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

/**
 * Whether this file can be a product's walkthrough.
 *
 * Every refusal says what to do next, because the person reading it is a
 * roofer with a video that came off a phone, not somebody who will go and look
 * up what a container format is.
 */
export function checkProductVideo(file: UploadCandidate): VideoCheck {
  const type = (file.type || "").toLowerCase();
  const looksMp4 = /\.(mp4|m4v)$/i.test(file.name);

  // The iPhone case gets its own sentence, because it is the common one and
  // "that is a quicktime file" means nothing to the person holding the phone.
  if (type === "video/quicktime" || (type === "" && /\.mov$/i.test(file.name)))
    return {
      ok: false,
      problem:
        "That is an iPhone .mov, which many Android phones and Windows " +
        "computers will not play. Product videos have to be MP4 — export or " +
        "share it as MP4 (CapCut does this) and try again.",
    };

  // The type is trusted only when the browser gave one. Some Androids hand
  // over an empty string for a file the user picked out of their gallery, and
  // refusing that would be refusing the ordinary case.
  if (
    type !== "" &&
    !VIDEO_TYPES.includes(type as (typeof VIDEO_TYPES)[number])
  )
    return {
      ok: false,
      problem: `That is a ${type.split("/")[1] ?? type} file. Product videos have to be MP4 — export or share it as MP4 and try again.`,
    };
  if (type === "" && !looksMp4)
    return {
      ok: false,
      problem:
        "That does not look like a video. Product videos have to be MP4 files.",
    };

  if (file.size > MAX_VIDEO_BYTES)
    return {
      ok: false,
      problem:
        `That video is ${mb(file.size)} and the limit is ${mb(MAX_VIDEO_BYTES)}. ` +
        `Export it again at 720p — the ones already on here are about a minute long and 15 MB.`,
    };

  if (file.size > LARGE_VIDEO_BYTES)
    return {
      ok: true,
      warning:
        `${mb(file.size)} is large for a video a customer opens on their phone. ` +
        `The ones already on here are about 15 MB. Upload it anyway?`,
    };

  return { ok: true, warning: null };
}

export function checkProductPoster(file: UploadCandidate): VideoCheck {
  const type = (file.type || "").toLowerCase();
  if (!POSTER_TYPES.includes(type as (typeof POSTER_TYPES)[number]))
    return {
      ok: false,
      problem: "The still has to be a JPEG, PNG or WebP image.",
    };
  if (file.size > MAX_POSTER_BYTES)
    return {
      ok: false,
      problem: `That image is ${mb(file.size)}. Keep the still under ${mb(MAX_POSTER_BYTES)}.`,
    };
  return { ok: true, warning: null };
}

/**
 * The object name one upload is stored under — a new one every time.
 *
 * Under the product's folder, so somebody looking at the bucket in a year can
 * tell whose it is, and stamped with the moment of upload so no two uploads
 * ever share a name. That second part is the point. A proposal already sent
 * carries the public URL of the video and still that were live when it was
 * frozen (ADR 0032); a fixed name like `walkthrough.mp4`, overwritten on
 * replacement, would change what a customer watches under a document that is
 * supposed to be a record. So an object is written once and never again, and
 * replacing a walkthrough points the product at a new file.
 *
 * The extension is fixed because the check above has already refused anything
 * that is not MP4, and a captured still is always a JPEG. An uploaded PNG
 * still is stored under `.jpg` too; the browser reads the content type the
 * upload carries, not the name.
 */
export function productVideoPath(
  productId: string,
  kind: "video" | "poster",
  at: Date,
): string {
  const stamp = at.toISOString().replace(/[-:.]/g, "");
  return kind === "video"
    ? `products/${productId}/${stamp}.mp4`
    : `products/${productId}/${stamp}-still.jpg`;
}

/**
 * The heading a customer reads over the video.
 *
 * Falls back to the catalogue name only so a video uploaded in a hurry still
 * appears with something over it — "Pipeboot Replacement Repair" is a warehouse
 * label and the box asking for a better one says so.
 */
export function productVideoTitle(
  title: string | null | undefined,
  productName: string,
): string {
  const written = (title ?? "").trim();
  return written === "" ? productName : written;
}
